import React, { useCallback, useMemo, useState } from "react";

import {
  StyledIconButton,
  StyledList,
  StyledListItem,
  StyledListItemName,
  StyledSortIcon,
  StyledSortIconDown,
  StyledSortIconUp,
  StyledSortWrapper,
  StyledValueButton,
} from "./Styles";
import { Menu } from "../Menu";
import { ElementWithRef } from "../Select";

export type SortDirections = "asc" | "desc";

export type SortOption = {
  id: string;
  name: string;
  defaultDirection: SortDirections;
};

export type SortObj = { sortBy: string; sortDirection: SortDirections };

export interface SortProps {
  label: string;
  icon?: React.ReactNode;
  options: SortOption[];
  selected: SortObj;
  onChange: (sort: SortObj) => void;
  target?: ElementWithRef<Element>;
  className?: string;
}

export const Sort: React.FC<SortProps> = ({
  label,
  icon,
  options,
  selected,
  onChange,
  target,
  className,
}) => {
  const [open, setOpen] = useState(false);

  const handleClose = useCallback(() => setOpen(false), []);
  const handleOpen = useCallback(() => setOpen(true), []);

  const handleOnChange = useCallback(
    (event: React.MouseEvent<HTMLLIElement>) => {
      const nextSortBy = event.currentTarget.dataset.id as string;
      let nextOrder: SortDirections =
        selected.sortDirection === "desc" ? "asc" : "desc";

      if (nextSortBy !== selected.sortBy) {
        nextOrder =
          options.find((opt) => opt.id === nextSortBy)?.defaultDirection ||
          "asc";
      }

      onChange({ sortBy: nextSortBy, sortDirection: nextOrder });
    },
    [onChange, selected, options]
  );

  const labelText = useMemo(() => {
    return options.find((opt) => opt.id === selected.sortBy)?.name ?? "";
  }, [selected.sortBy, options]);

  if (!labelText) {
    console.warn("Cannot find selected id in options.");
  }

  return (
    <Menu
      onOpen={handleOpen}
      onClose={handleClose}
      position="bottom-end"
      target={
        target || (
          <StyledSortWrapper className={className}>
            <StyledValueButton
              active={open}
              value={labelText}
              label={label}
              data-testid="sort-button"
            />
            <StyledIconButton
              active={open}
              variant="text gray"
              data-testid="sort-icon-button"
            >
              {icon || <StyledSortIcon />}
            </StyledIconButton>
          </StyledSortWrapper>
        )
      }
    >
      <StyledList>
        {options.map(({ id, name, defaultDirection }) => {
          let showDownArrow = selected.sortDirection === "asc";

          if (selected.sortBy !== id) {
            showDownArrow = defaultDirection === "asc";
          }

          return (
            <StyledListItem
              key={id}
              onClick={handleOnChange}
              data-id={id}
              data-testid={id}
              $active={id === selected.sortBy}
            >
              <StyledListItemName>{name}</StyledListItemName>
              {showDownArrow ? <StyledSortIconDown /> : <StyledSortIconUp />}
            </StyledListItem>
          );
        })}
      </StyledList>
    </Menu>
  );
};
